/**
 * <collection-page data-section="SECTION ID">   (sections/main-collection.liquid)
 *
 * 1. The category row. It docks under the header while the shopper scrolls the products
 *    (CSS `position: sticky`). This script only says WHEN it is docked, so the small title
 *    can fade in, and which edge of the row still has links to scroll to, so that edge fades.
 *    Both use IntersectionObserver: nothing is measured while scrolling.
 * 2. Switching category. A tap on a category loads that collection's section from Shopify,
 *    swaps the title and the products in place, updates the address and the tab's title, and
 *    brings the top of the products back under the row. The browser's back button restores
 *    the previous category. Without JavaScript the links are plain links.
 * 3. "Show more". Loads the next page and adds its products to the grid: in-stock ones before
 *    the sold-out ones already shown, sold-out ones at the end.
 * 4. No waiting, so nothing jumps. The next page is fetched as the button comes near, and a
 *    category as soon as a finger or the pointer lands on its link, so the products are
 *    usually there by the time he lets go. While products are swapped the space they took is
 *    held, so the footer never slides into view. What he leaves is kept as he left it, so the
 *    back button shows it again at once.
 *
 * Every [data-refresh="…"] region inside the element is replaced by the region of the same
 * name in what Shopify sends back.
 */
/** How long a fetched page is kept, in milliseconds. */
const KEEP = 120000;

class CollectionPage extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-category-bar]');
    this.list = this.querySelector('[data-category-list]');
    this.pages = new Map();
    this.shown = this.keyOf(window.location.href);
    this.addEventListener('click', (event) => this.onClick(event));
    for (const sign of ['pointerover', 'touchstart', 'focusin']) this.addEventListener(sign, (event) => this.warm(event), { passive: true });
    this.watchMore();
    this.onPopState = () => this.load(window.location.href, { push: false });
    window.addEventListener('popstate', this.onPopState);
    if (!this.bar) return;

    this.watchDock = this.watchDock.bind(this);
    document.addEventListener('header:resize', this.watchDock);
    this.watchDock();
    this.watchEdges();
    this.centre(false);
    // The links get a little wider once the font has arrived.
    document.fonts?.ready.then(() => this.isConnected && this.centre(false));
    // On wide screens the docked links sit in the middle of the bar, after the small title.
    // How far that is depends only on widths, so it is worked out when a size changes.
    this.sizeObserver = new ResizeObserver(() => this.placeDocked());
    this.sizeObserver.observe(this.bar);
    this.sizeObserver.observe(this.list);
  }

  placeDocked() {
    if (!this.bar) return;
    const inner = this.list.parentElement;
    const title = this.bar.querySelector('.category-bar__title');
    const style = getComputedStyle(inner);
    const room = inner.clientWidth - parseFloat(style.paddingInlineStart) - parseFloat(style.paddingInlineEnd);
    const free = room - this.list.scrollWidth;
    const afterTitle = (title?.offsetWidth || 0) + 24;
    const shift = free <= 0 ? 0 : Math.min(free, Math.max(free / 2, afterTitle));
    this.bar.style.setProperty('--dock-shift', `${Math.round(shift)}px`);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('header:resize', this.watchDock);
    this.dockObserver?.disconnect();
    this.edgeObserver?.disconnect();
    this.sizeObserver?.disconnect();
    this.moreObserver?.disconnect();
  }

  /** A finger or the pointer is on a category: fetch it now, before he lets go. */
  warm(event) {
    const link = event.target instanceof Element ? event.target.closest('[data-category-link]') : null;
    if (link && link.getAttribute('aria-current') !== 'page') this.fetchSection(link.href).catch(() => {});
  }

  /** Fetches the next page as "Show more" comes near, so a tap on it shows the products at once. */
  watchMore() {
    this.moreObserver?.disconnect();
    const button = this.querySelector('[data-show-more]');
    if (!button || !('IntersectionObserver' in window)) return;
    this.moreObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.moreObserver.disconnect();
        this.fetchSection(button.href).catch(() => {});
      },
      { rootMargin: '150% 0px' }
    );
    this.moreObserver.observe(button);
  }

  /**
   * Says when the row is docked. The line it docks at is where the row sticks, the header's
   * full height, and it does not move when the header slides away. The 8px mark gives it some
   * slack: docked once the whole mark is above the line, free again only once the whole mark is
   * below it. Rebuilt only when the header itself changes height.
   */
  watchDock() {
    const mark = this.querySelector('[data-dock-mark]');
    if (!mark || !('IntersectionObserver' in window)) return;
    this.dockObserver?.disconnect();
    const line = parseFloat(getComputedStyle(this.bar).top) || 0;
    this.dockObserver = new IntersectionObserver(
      ([entry]) => {
        const edge = entry.rootBounds ? entry.rootBounds.top : line;
        if (entry.boundingClientRect.bottom <= edge) this.bar.classList.add('is-docked');
        else if (entry.boundingClientRect.top >= edge) this.bar.classList.remove('is-docked');
      },
      { rootMargin: `-${line}px 0px 0px 0px`, threshold: [0, 1] }
    );
    this.dockObserver.observe(mark);
  }

  /** Which edge of the row has more links off screen: that edge gets a soft fade. */
  watchEdges() {
    const items = this.list.children;
    if (!items.length || !('IntersectionObserver' in window)) return;
    const first = items[0];
    const last = items[items.length - 1];
    this.edgeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.list.classList.toggle(entry.target === first ? 'has-more-start' : 'has-more-end', entry.intersectionRatio < 0.98);
      },
      { root: this.list, threshold: [0.98, 1] }
    );
    this.edgeObserver.observe(first);
    this.edgeObserver.observe(last);
  }

  /** Brings the current category into the middle of the row. */
  centre(smooth) {
    const current = this.list?.querySelector('[aria-current="page"]');
    if (!current || this.list.scrollWidth <= this.list.clientWidth) return;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const link = current.getBoundingClientRect();
    const row = this.list.getBoundingClientRect();
    // Measured on screen, so the same sum works right to left.
    this.list.scrollBy({ left: link.left + link.width / 2 - (row.left + row.width / 2), behavior: smooth && !calm ? 'smooth' : 'auto' });
  }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    const category = target?.closest('[data-category-link]');
    if (category) {
      event.preventDefault();
      // Was the row docked under the header when he tapped (the tracking plan's `nav_docked`)?
      if (category.getAttribute('aria-current') !== 'page') this.load(category.href, { push: true, docked: Boolean(this.bar?.classList.contains('is-docked')) });
      return;
    }
    const more = target?.closest('[data-show-more]');
    if (more) {
      event.preventDefault();
      this.showMore(more);
    }
  }

  /** The name a page is kept under: its path and query. */
  keyOf(address) {
    const url = new URL(address, window.location.origin);
    return url.pathname + url.search;
  }

  /**
   * Asks Shopify for this section alone, as it would look at `address`. An answer is kept for
   * two minutes, so asking early (see `warm` and `watchMore`) and going back cost nothing.
   */
  fetchSection(address) {
    const url = new URL(address, window.location.origin);
    const key = this.keyOf(address);
    url.searchParams.set('section_id', this.dataset.section);
    const kept = this.pages.get(key);
    if (kept && Date.now() - kept.at < KEEP) return kept.page;
    const page = fetch(url, { headers: { Accept: 'text/html' } }).then(async (response) => {
      if (!response.ok) throw new Error(`Collection request failed (${response.status})`);
      return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('collection-page');
    });
    this.pages.set(key, { at: Date.now(), page });
    page.catch(() => this.pages.delete(key));
    return page;
  }

  /** Shows another collection in place. Falls back to a normal page load if anything goes wrong. */
  async load(address, { push, docked = false }) {
    // The back button also fires for a jump inside the page: nothing to load then.
    if (this.keyOf(address) === this.shown) return;
    const results = this.querySelector('[data-refresh="results"]');
    const ticket = {};
    this.loading = ticket;
    results?.setAttribute('aria-busy', 'true');
    try {
      const fresh = await this.fetchSection(address);
      if (this.loading !== ticket) return;
      if (!fresh) throw new Error('No collection in the answer');
      // What he is leaving is kept exactly as it is (the extra pages he opened too), so going
      // back shows it in the same breath, before the browser draws anything else.
      this.pages.set(this.shown, { at: Infinity, page: Promise.resolve(this.cloneNode(true)) });
      // The products keep the room they had while they change, so nothing under them moves up.
      if (results) results.style.minBlockSize = `${results.offsetHeight}px`;
      for (const region of this.querySelectorAll('[data-refresh]')) {
        const replacement = fresh.querySelector(`[data-refresh="${region.dataset.refresh}"]`);
        if (replacement) region.innerHTML = replacement.innerHTML;
      }
      const path = new URL(address, window.location.origin).pathname;
      for (const link of this.querySelectorAll('[data-category-link]')) {
        if (new URL(link.href).pathname === path) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      }
      this.dataset.tabTitle = fresh.dataset.tabTitle || '';
      this.dataset.category = fresh.dataset.category || '';
      this.dataset.handle = fresh.dataset.handle || '';
      this.dataset.count = fresh.dataset.count || '';
      if (this.dataset.tabTitle) document.title = this.dataset.tabTitle;
      if (push) window.history.pushState({}, '', address);
      this.shown = this.keyOf(address);
      // A collection shown without a page load is a collection viewed all the same (the
      // tracking plan). By the row of categories; with the Back button, by nothing he tapped.
      if (this.dataset.handle) {
        window.kz?.track?.('Collection Viewed', { collection_handle: this.dataset.handle, product_count: Number(this.dataset.count) || 0, source: push ? 'category_bar' : 'direct', nav_docked: push ? docked : false });
      }
      // Anything else on the page that depends on the category (the row of suggestions).
      document.dispatchEvent(new CustomEvent('collection:change'));
      this.centre(true);
      this.placeDocked();
      this.watchMore();
      if (!results) return;
      // From here on the products fill at least one screen (CSS), so a short category
      // does not pull the footer into view.
      results.classList.add('is-switched');
      const release = () => results.style.removeProperty('min-block-size');
      // Back to the top of the products, but only if he had scrolled past it. With the back
      // button the browser puts him where he was.
      if (push && results.getBoundingClientRect().top < 0) {
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        results.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' });
        if (calm || !('onscrollend' in window)) window.setTimeout(release, calm ? 0 : 700);
        else window.addEventListener('scrollend', release, { once: true });
      } else release();
    } catch (error) {
      console.error('Kanze: could not switch category in place.', error);
      if (this.loading === ticket) window.location.assign(address);
    } finally {
      if (this.loading === ticket) this.querySelector('[data-refresh="results"]')?.removeAttribute('aria-busy');
    }
  }

  /** Adds the next page of products under the ones already shown. */
  async showMore(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    if (this.dataset.handle) {
      window.kz?.track?.('Collection Show More Tapped', { collection_handle: this.dataset.handle, shown_before: this.querySelectorAll('[data-grid] > .collection__item').length });
    }
    try {
      const fresh = await this.fetchSection(button.href);
      const grid = this.querySelector('[data-grid]');
      // The page's cards, and the look photos that stand between them (snippets/collection-look.liquid).
      const incoming = fresh ? [...fresh.querySelectorAll('[data-grid] > .collection__item, [data-grid] > .collection-look')] : [];
      if (!grid || !incoming.length) throw new Error('No products in the answer');
      const firstSoldOut = grid.querySelector(':scope > [data-sold-out]');
      let firstNew = null;
      for (const item of incoming) {
        // "One card per piece": a colour of a piece that already has its card is left out.
        if (item.dataset.piece && [...grid.children].some((shown) => shown.dataset.piece === item.dataset.piece)) continue;
        const node = document.importNode(item, true);
        if (node.matches('.collection__item')) firstNew = firstNew || node;
        if (node.hasAttribute('data-sold-out') || !firstSoldOut) grid.append(node);
        else grid.insertBefore(node, firstSoldOut);
      }
      const more = this.querySelector('[data-more]');
      const freshMore = fresh.querySelector('[data-more]');
      if (more && freshMore) more.innerHTML = freshMore.innerHTML;
      else more?.remove();
      this.watchMore();
      // Keyboard users carry on from the first new product.
      window.Kanze.focus(firstNew?.querySelector('a.card__link'));
    } catch (error) {
      console.error('Kanze: could not load more products in place.', error);
      window.location.assign(button.href);
    }
  }
}

if (!customElements.get('collection-page')) customElements.define('collection-page', CollectionPage);
